import { useCallback, useReducer } from 'react';

import useMfa from 'api/useMfa';
import { EToasterStatus, showToast } from 'utils/showToast';

import {
  ELockoutType,
  EMfaActionType,
  LockoutState,
  MfaAction,
  MfaErrorResponse,
  MfaState
} from '../LoginModal.types';

const initialMfaState: MfaState = {
  session: {
    email: '',
    password: '',
    rememberMe: true,
    mfaSessionToken: null,
    mfaEnabled: false
  },
  attempts: {
    maxAttempts: 5,
    currentAttempt: 0
  },
  lockout: {
    endTime: null,
    type: null
  }
};

const mfaActionHandlers: Record<EMfaActionType, (state: MfaState, action: MfaAction) => MfaState> = {
  [EMfaActionType.SET_SESSION]: (state, action) => {
    if (action.type !== EMfaActionType.SET_SESSION) return state;
    return {
      ...state,
      session: {
        email: action.payload.email,
        password: action.payload.password,
        rememberMe: action.payload.rememberMe,
        mfaSessionToken: action.payload.mfaSessionToken,
        mfaEnabled: action.payload.mfaEnabled
      },
      attempts: {
        maxAttempts: action.payload.maxAttempts,
        currentAttempt: action.payload.currentAttempt
      },
      lockout: {
        endTime: null,
        type: null
      }
    };
  },
  [EMfaActionType.SET_LOCKOUT]: (state, action) => {
    if (action.type !== EMfaActionType.SET_LOCKOUT) return state;
    return {
      ...state,
      lockout: {
        endTime: action.payload.endTime,
        type: action.payload.type
      }
    };
  },
  [EMfaActionType.CLEAR_LOCKOUT]: (state) => ({
    ...state,
    lockout: {
      endTime: null,
      type: null
    },
    attempts: {
      ...state.attempts,
      currentAttempt: 0
    }
  }),
  [EMfaActionType.UPDATE_ATTEMPTS]: (state, action) => {
    if (action.type !== EMfaActionType.UPDATE_ATTEMPTS) return state;
    return {
      ...state,
      attempts: {
        maxAttempts: action.payload.max,
        currentAttempt: action.payload.current
      }
    };
  },
  [EMfaActionType.RESET_ATTEMPTS]: (state) => ({
    ...state,
    attempts: {
      ...state.attempts,
      currentAttempt: 0
    }
  }),
  [EMfaActionType.UPDATE_SESSION_TOKEN]: (state, action) => {
    if (action.type !== EMfaActionType.UPDATE_SESSION_TOKEN) return state;
    return {
      ...state,
      session: {
        ...state.session,
        mfaSessionToken: action.payload
      }
    };
  }
};

const mfaReducer = (state: MfaState, action: MfaAction): MfaState => {
  const handler = mfaActionHandlers[action.type];
  return handler ? handler(state, action) : state;
};

interface UseMfaFlowProps {
  onLoginSuccess: (response: any, rememberMe: boolean) => void;
  onRateLimitError: (error: any, lockoutType: ELockoutType) => boolean;
}

export const useMfaFlow = ({ onLoginSuccess, onRateLimitError }: UseMfaFlowProps) => {
  const [mfaState, dispatch] = useReducer(mfaReducer, initialMfaState);
  const { verifyMfa, skipMfa, resendMfa } = useMfa();

  const handleMfaVerify = useCallback(
    async (code: string): Promise<boolean> => {
      if (!mfaState.session.mfaSessionToken) {
        showToast({
          message: 'Invalid session. Please log in again.',
          status: EToasterStatus.ERROR
        });
        return false;
      }

      try {
        const response = await verifyMfa.mutateAsync({
          mfaSessionToken: mfaState.session.mfaSessionToken,
          code
        });

        onLoginSuccess(response, mfaState.session.rememberMe);
        return true;
      } catch (error: any) {
        if (onRateLimitError(error, ELockoutType.OTP)) {
          return false;
        }

        if (error?.response?.status === 401) {
          const errorData = error.response.data as MfaErrorResponse;
          const newCurrentAttempt = errorData.maxAttempts - errorData.attemptsRemaining;
          dispatch({
            type: EMfaActionType.UPDATE_ATTEMPTS,
            payload: {
              current: newCurrentAttempt,
              max: errorData.maxAttempts
            }
          });
        }
        return false;
      }
    },
    [
      mfaState.session.mfaSessionToken,
      mfaState.session.rememberMe,
      verifyMfa,
      onLoginSuccess,
      onRateLimitError
    ]
  );

  const handleMfaSkip = useCallback(async (): Promise<void> => {
    if (!mfaState.session.mfaSessionToken) {
      showToast({
        message: 'Invalid session. Please log in again.',
        status: EToasterStatus.ERROR
      });
      return;
    }

    try {
      const response = await skipMfa.mutateAsync({
        mfaSessionToken: mfaState.session.mfaSessionToken,
        rememberMe: mfaState.session.rememberMe ?? true
      });

      onLoginSuccess(response, mfaState.session.rememberMe);
    } catch (error) {
      showToast({
        message: 'Failed to skip MFA. Please try again.',
        status: EToasterStatus.ERROR
      });
    }
  }, [mfaState.session.mfaSessionToken, mfaState.session.rememberMe, skipMfa, onLoginSuccess]);

  const handleMfaResend = useCallback(async (): Promise<void> => {
    if (!mfaState.session.mfaSessionToken) {
      showToast({
        message: 'Invalid session. Please log in again.',
        status: EToasterStatus.ERROR
      });
      return;
    }

    try {
      const response = await resendMfa.mutateAsync({
        mfaSessionToken: mfaState.session.mfaSessionToken,
        email: mfaState.session.email
      });

      dispatch({ type: EMfaActionType.UPDATE_SESSION_TOKEN, payload: response.mfaSessionToken });
    } catch (error: any) {
      if (!onRateLimitError(error, ELockoutType.OTP)) {
        showToast({
          message: 'Failed to resend code. Please try again.',
          status: EToasterStatus.ERROR
        });
      }
    }
  }, [
    mfaState.session.mfaSessionToken,
    mfaState.session.email,
    resendMfa,
    onRateLimitError
  ]);

  const setMfaSession = useCallback(
    (sessionData: {
      email: string;
      password: string;
      rememberMe: boolean;
      mfaSessionToken: string;
      maxAttempts: number;
      currentAttempt: number;
      mfaEnabled: boolean;
    }): void => {
      dispatch({ type: EMfaActionType.SET_SESSION, payload: sessionData });
    },
    []
  );

  const setLockout = useCallback((lockout: LockoutState): void => {
    if (lockout.endTime && lockout.type) {
      dispatch({ type: EMfaActionType.SET_LOCKOUT, payload: { endTime: lockout.endTime, type: lockout.type } });
    } else {
      dispatch({ type: EMfaActionType.CLEAR_LOCKOUT });
    }
  }, []);

  return {
    mfaState,
    handleMfaVerify,
    handleMfaSkip,
    handleMfaResend,
    setMfaSession,
    setLockout,
    isLoading: verifyMfa.isLoading || skipMfa.isLoading
  };
};

